// v0.6.12 A5-27 — pick a calculation, don't type its id.
//
// Four boxes asked the operator to paste an internal calculation id
// (placeholder "calc_xxxx-xxxx-xxxx"): the two amendment modals and the
// two "filed in a later calculation" backlog forms. Every acceptable
// answer is a row the app already holds — GET /runs/calc returns them,
// with `frozen`, `superseded_by_calc_id` and `filing_reference` — so the
// box was asking the operator to copy out something the screen could
// simply offer. A typed id could also name a calculation the server then
// refused (not finalised, or replaced by a later one), which the
// operator only learned after submitting.
//
// Each caller passes the same conditions its endpoint enforces as
// `filter`, so the list holds only calculations the server will accept.
// Options are labelled by filing reference, client and period — never
// the raw id. GET /runs/calc returns the newest 200, which covers a
// correction or a later filing (both are by definition recent).
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { listCalcRuns } from "../lib/api";

const SELECT_CLS =
  "w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 "
  + "text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs tabular-nums";

// The app's pinned day-first convention (see DateInput).
const dayFirst = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

// UK and Greece have two spellings in the wild; compare on one.
const normCc = (cc) => {
  const c = String(cc || "").toUpperCase();
  return c === "UK" ? "GB" : c === "EL" ? "GR" : c;
};

export const calcPeriodLabel = (c) => {
  const a = dayFirst(c?.period_start);
  const b = dayFirst(c?.period_end);
  return a && b ? `${a} – ${b}` : (a || b);
};

// Where a dropped row was filed later: any calculation the backlog
// endpoints accept (routes_excluded_registry: it must exist; the bulk
// route also refuses the source calculation itself). Narrowed to the
// same client as the calculation the row was dropped from, and to ones
// not replaced by a later version. Unfinalised calculations stay in the
// list on purpose — the endpoint accepts them, and the later return may
// not be finalised yet when this one has to be exported.
export const laterFilingFilter = (sourceCalcId) => (c, all) => {
  if (c.id === sourceCalcId || c.superseded_by_calc_id) return false;
  const src = (all || []).find((x) => x.id === sourceCalcId);
  const cid = src?.client_id;
  return !cid || !c.client_id || c.client_id === cid;
};

// A corrective filing for an amendment: finalised, not replaced, for the
// amendment's country, and for the same client as the filing it corrects.
// Mirrors routes_admin_amendments._validate_frozen_calc (frozen, not
// superseded); the country and client conditions only narrow the list.
export const correctiveCalcFilter = (amendment) => (c, all) => {
  if (!c.frozen || c.superseded_by_calc_id) return false;
  const ac = normCc(amendment?.country);
  const cf = normCc(c.country_filter || "ALL");
  if (ac && cf && cf !== "ALL" && cf !== ac) return false;
  const affected = amendment?.affected_frozen_calc_ids || [];
  if (affected.includes(c.id)) return false;
  const clients = new Set(
    (all || []).filter((x) => affected.includes(x.id) && x.client_id)
      .map((x) => x.client_id),
  );
  return clients.size === 0 || !c.client_id || clients.has(c.client_id);
};

export const FiledCalcSelect = ({
  testid, value, onChange, filter = null, disabled = false,
  "aria-label": ariaLabel, className = "",
}) => {
  const { t } = useTranslation();
  const [runs, setRuns] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let live = true;
    listCalcRuns()
      .then((d) => { if (live) setRuns(Array.isArray(d) ? d : []); })
      .catch(() => { if (live) { setRuns([]); setLoadFailed(true); } });
    return () => { live = false; };
  }, []);

  const all = runs || [];
  const options = filter ? all.filter((c) => filter(c, all)) : all;

  const notFinalised = t("ui.calc_picker.not_finalised", {
    defaultValue: "not finalised yet",
  });
  // An unfinalised calculation has no filing reference yet, so the day it
  // was run is what tells two of them apart.
  const label = (c) => [
    c.filing_reference
      || (c.created_at ? `${notFinalised}, ${dayFirst(c.created_at)}` : notFinalised),
    c.client_name || c.client_code || "",
    calcPeriodLabel(c),
  ].filter(Boolean).join(" · ");

  let blank;
  if (runs === null) {
    blank = t("ui.calc_picker.loading", { defaultValue: "Loading calculations…" });
  } else if (loadFailed) {
    blank = t("ui.calc_picker.load_failed", {
      defaultValue: "Could not load the calculations — close and try again",
    });
  } else if (options.length === 0) {
    blank = t("ui.calc_picker.none", {
      defaultValue: "No calculation fits — finalise the right one first",
    });
  } else {
    blank = t("ui.calc_picker.pick", { defaultValue: "Choose a calculation…" });
  }

  return (
    <select
      data-testid={testid}
      value={value}
      disabled={disabled || runs === null}
      aria-label={ariaLabel || t("ui.calc_picker.pick", {
        defaultValue: "Choose a calculation…",
      })}
      onChange={(e) => {
        const id = e.target.value;
        onChange(id, options.find((c) => c.id === id) || null);
      }}
      className={`${SELECT_CLS} ${className}`}
    >
      <option value="">{blank}</option>
      {options.map((c) => (
        <option key={c.id} value={c.id}>{label(c)}</option>
      ))}
    </select>
  );
};

export default FiledCalcSelect;
